import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const StyledList = styled.div`
  .description-title {
    margin: 0 0 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--ds-row-divider);
    font-size: 14px;
    font-weight: 700;
    color: var(--textColor);
  }

  dl {
    margin: 0;
  }

  dt {
    font-size: 12px;
    color: var(--dimGray);
  }

  dd {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--textColor);
    overflow-wrap: anywhere;
  }

  .description-extra {
    margin-top: 6px;
  }

  dd .ant-btn {
    max-width: 100% !important;
    height: auto !important;
    white-space: normal !important;
  }

  // Disposition verticale : grille qui s'adapte à la largeur disponible.
  // Les "span" hérités du tableau antd ne sont plus appliqués : ils étaient
  // incohérents d'un écran à l'autre et empilaient les valeurs.
  .description-vertical {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(150px, 100%), 1fr));
    gap: 18px 24px;

    dt {
      margin-bottom: 4px;
    }

    // Texte long (description…) : toute la largeur, lecture confortable
    .description-full {
      grid-column: 1 / -1;

      dd {
        max-width: 75ch;
        font-weight: 400;
        line-height: 1.6;
        overflow-wrap: break-word;

        p {
          margin: 0 0 8px;
        }

        p:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  // Disposition horizontale : libellé à gauche, valeur à droite
  .description-horizontal {
    display: grid;
    grid-template-columns: minmax(120px, max-content) minmax(0, 1fr);
    gap: 10px 20px;
    align-items: baseline;

    > div {
      display: contents;
    }

    @media (max-width: 576px) {
      grid-template-columns: minmax(0, 1fr);
      gap: 2px;

      dd {
        margin-bottom: 10px;
      }
    }
  }

  .description-comment {
    margin-top: 16px;
    font-weight: 600;
  }
`;

/**
 * List of label / value pairs used in the detail pages.
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.title - Optional title of the list.
 * @param {Object[]} props.data - Items: { label, content, extra, hidden,
 * fullWidth }. `fullWidth` spans the whole grid (long texts).
 * @param {boolean} props.skipEmpty - Hide the empty items flagged as hidden.
 * @param {boolean} props.translate - Translate the labels.
 * @param {string} props.layout - 'vertical' (grid) or 'horizontal' (rows).
 * @param {React.ReactNode} props.comment - Content displayed under the list.
 * @returns {JSX.Element} - The DescriptionList component.
 */
export const DescriptionList = ({
  title,
  data,
  skipEmpty,
  translate,
  layout,
  comment
}) => {
  const { t } = useTranslation();
  const items = data.filter(
    (item) => item.content !== '-' || !skipEmpty || !item.hidden
  );

  return (
    <StyledList>
      {title && <div className="description-title">{title}</div>}
      <dl
        className={
          layout === 'horizontal'
            ? 'description-horizontal'
            : 'description-vertical'
        }
      >
        {items.map((item) => (
          <div
            key={item.label}
            className={item.fullWidth ? 'description-full' : undefined}
          >
            <dt>{translate ? t(`${item.label}`) : item.label}</dt>
            <dd>
              {item.content || '-'}
              {item.extra && (
                <div className="description-extra">{item.extra}</div>
              )}
            </dd>
          </div>
        ))}
      </dl>
      {comment && <div className="description-comment">{comment}</div>}
    </StyledList>
  );
};

DescriptionList.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  skipEmpty: PropTypes.bool,
  translate: PropTypes.bool,
  layout: PropTypes.string,
  comment: PropTypes.node
};

DescriptionList.defaultProps = {
  title: null,
  skipEmpty: false,
  translate: false,
  layout: 'vertical',
  comment: null
};
